import { setJsonQuery, setKpId } from 'shared/slices/jsonQuery';
import { TAsyncThunkAction } from 'shared/types/redux';
import { updateSearchMeta } from '../../searchMeta';
import { ICircleObject, IPolygonObject, TGeoObject } from 'shared/types/geoSuggest';
import { selectJsonQuery } from 'shared/selectors/jsonQuery';
import { getGeoValue, JsonQuery } from '../../../../packages/JsonQuery';
import {
  circlesAreEqual,
  coordsArrayNumberToString,
  coordsIsEqual,
  coordsNumberToString,
  needClearGeo,
} from 'shared/utils/geo';
import { selectLocation } from 'shared/selectors/location';
import { EMetroTravelType } from '../../../../packages/api-models/common/json_query';
import { setUndergroundTravel } from '../../../../packages/JsonQuery/setUndergroundTravel';
import { getDefaultTravelTimeForType } from 'shared/utils/underground';

const isPolygonObject = (value: TGeoObject): value is IPolygonObject => 'polygon' in value;
const isCircleObject = (value: TGeoObject): value is ICircleObject => 'center' in value;

export const updateGeo =
  (value: TGeoObject): TAsyncThunkAction =>
  async (dispatch, getState) => {
    const state = getState();
    const currentLocation = selectLocation(state);
    const jsonQuery = selectJsonQuery(state);

    let nextJsonQuery = new JsonQuery(jsonQuery).toJSON();

    let geo = getGeoValue()(nextJsonQuery) || [];

    if (!isPolygonObject(value) && !isCircleObject(value)) {
      if (geo.filter(geoValue => geoValue.type === value.type && geoValue.id === value.id).length > 0) {
        return void 0;
      }

      if (value.locationInfo && needClearGeo(value.locationInfo, currentLocation)) {
        geo = [];
      }

      switch (value.type) {
        case 'location':
          if (!value.locationInfo || value.locationInfo.id !== value.id) {
            if (!nextJsonQuery.region || !nextJsonQuery.region.value.includes(value.regionId)) {
              nextJsonQuery.region = { type: 'terms', value: [value.regionId] };
              geo = [];
            }

            geo.push({
              type: value.type,
              id: value.id,
            });
          }
          break;

        case 'street':
          geo.push({
            type: value.type,
            id: value.id,
          });
          break;

        case 'highway':
          geo.push({
            type: value.type,
            id: value.id,
          });
          break;

        case 'district':
          geo.push({
            type: value.type,
            id: value.id,
          });
          break;

        case 'underground':
          geo.push({
            type: value.type,
            id: value.id,
          });
          break;

        case 'house':
          geo.push({
            type: value.type,
            id: value.id,
          });
          break;

        case 'newobject':
          geo.push({
            type: value.type,
            id: value.id,
          });
          break;

        case 'railway':
          geo.push({
            type: value.type,
            id: value.id,
          });
          break;

        case 'regional_district_id':
          geo.push({
            type: value.type,
            id: value.id,
          });
          break;

        default:
          break;
      }
    } else if (isPolygonObject(value)) {
      if (
        geo.filter(
          geoValue =>
            geoValue.type === 'polygon' &&
            coordsIsEqual(geoValue.coordinates, value.polygon ? coordsArrayNumberToString(value.polygon) : []),
        ).length > 0
      ) {
        return void 0;
      }

      geo.push({
        type: 'polygon',
        name: value.text,
        coordinates: value.polygon ? coordsArrayNumberToString(value.polygon) : [],
      });
    } else if (isCircleObject(value)) {
      if (
        geo.filter(
          geoValue =>
            geoValue.type === 'distance' &&
            circlesAreEqual(geoValue, { radius: value.radius, center: coordsNumberToString(value.center) }),
        ).length > 0
      ) {
        return void 0;
      }

      geo.push({
        type: 'distance',
        name: value.text,
        radius: value.radius,
        center: coordsNumberToString(value.center),
      });
    }

    if (value.type === 'underground') {
      const wasNoUndergrounds = !nextJsonQuery.geo || !nextJsonQuery.geo.value.some(geo => geo.type === 'underground');
      const wasNoTravelType = !nextJsonQuery.only_foot || !nextJsonQuery.only_foot.value;
      const wasNoTravelTime = !nextJsonQuery.foot_min || !nextJsonQuery.foot_min.value;

      if (wasNoUndergrounds && wasNoTravelTime && wasNoTravelType) {
        nextJsonQuery = setUndergroundTravel(jsonQuery)(
          EMetroTravelType.MapFoot,
          getDefaultTravelTimeForType(EMetroTravelType.MapFoot),
        );
      }
    }

    nextJsonQuery.geo = { type: 'geo', value: geo };

    dispatch(setKpId(null));
    dispatch(setJsonQuery(nextJsonQuery));
    await dispatch(updateSearchMeta());
  };
